import { useCallback, useState, type FocusEvent, type JSX, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';

/**
 * L'ETIQUETTE AU SURVOL : le nom d'un bouton qui n'affiche qu'une icone.
 *
 * D'OU ELLE VIENT. Le dock a trois comportements (`dock.mode`), et l'un d'eux
 * garde le rail REPLIE en permanence : des icones, rien d'autre. Le nom de
 * chaque app vivait alors dans l'attribut `title` du navigateur, ce qui n'est
 * pas un choix de design, c'est une absence de choix : il sort au bout d'une a
 * deux secondes (le navigateur decide, pas nous), il se peint dans la police et
 * les couleurs du systeme, il ignore le theme, et on ne peut ni l'accelerer ni
 * le styler. Quelqu'un qui garde son dock ferme devait donc attendre, ou
 * deviner.
 *
 * CE QU'ELLE N'EST PAS : ni un popover (aucun contenu interactif, aucune
 * fermeture a negocier, rien a cliquer dedans), ni une aide contextuelle. Elle
 * ne dit QUE le nom de la chose survolee. Un texte qui a besoin d'une phrase
 * n'est pas une etiquette, c'est une aide, et elle se pose autrement.
 *
 * ELLE VIT DANS UN PORTAL, ET CE N'EST PAS UN LUXE (trois raisons mesurees sur
 * le rail du dock, qui les cumule toutes les trois) :
 *   1. le rail porte une opacite (0,5 au repos, 1 au survol) : une etiquette
 *      rendue DEDANS en heriterait, donc elle serait a moitie transparente au
 *      repos et mettrait deux secondes a se regriser avec lui ;
 *   2. cette meme opacite cree un contexte d'empilement : un z-index pose sur
 *      l'etiquette ne la ferait pas passer au-dessus de la surface voisine,
 *      elle resterait sous le moindre element positionne de l'ecran d'a cote ;
 *   3. l'etiquette doit SORTIR de son rail (56 px de large) pour etre lisible.
 * Un `position: fixed` calcule depuis le rectangle du declencheur echappe aux
 * trois d'un coup, et se moque de la largeur du rail.
 *
 * ACCESSIBILITE : l'etiquette est `aria-hidden`. Le nom accessible du bouton
 * reste son `aria-label`, qui est deja la et que les lecteurs d'ecran
 * annoncent : reannoncer le meme texte par une etiquette visuelle le ferait
 * lire deux fois. Corollaire pour l'appelant : elle ne remplace JAMAIS un
 * `aria-label`, elle double sa presence a l'oeil.
 *
 * CE QU'ELLE NE FAIT PAS ENCORE, faute de cas reel : se retourner a gauche
 * quand la place manque a droite. Elle se pose a droite, point. Le rail du
 * dock est colle au bord gauche de l'ecran et l'etiquette est plafonnee en
 * largeur, donc elle ne sort jamais. Le jour ou un appelant la pose ailleurs,
 * c'est ce commentaire qu'il faudra corriger avant le code.
 */

/** L'ecart entre le bord droit du declencheur et l'etiquette (px). */
export const HOVER_LABEL_GAP_PX = 8;

/** Ce qu'il faut d'un rectangle pour poser l'etiquette. PURE, testable. */
export interface HoverLabelAnchor {
  top: number;
  right: number;
  height: number;
}

/** La position de l'etiquette, en coordonnees de viewport (`position: fixed`). */
export interface HoverLabelPlacement {
  top: number;
  left: number;
}

/**
 * Ou se pose l'etiquette : a DROITE du declencheur, centree sur sa hauteur.
 *
 * Le centrage vertical se finit en CSS (`translateY(-50%)`) et pas ici : la
 * hauteur de l'etiquette depend du texte rendu, donc elle n'est pas connue au
 * moment ou l'on mesure le declencheur. Calculer `top - hauteur / 2` en JS
 * demanderait un second rendu pour mesurer, c'est-a-dire une etiquette qui
 * saute d'un pixel sous l'oeil.
 */
export function hoverLabelPlacement(
  anchor: HoverLabelAnchor,
  gap: number = HOVER_LABEL_GAP_PX,
): HoverLabelPlacement {
  return { top: anchor.top + anchor.height / 2, left: anchor.right + gap };
}

/** Les gestes a poser sur un declencheur. A etaler tels quels sur le bouton. */
export interface HoverLabelTriggerProps {
  onMouseEnter: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onMouseDown: () => void;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
}

export interface HoverLabelBinding {
  /** Les gestes d'un declencheur, pour le libelle donne. */
  trigger: (label: string) => HoverLabelTriggerProps;
  /** Retire l'etiquette tout de suite (sortie du groupe, navigation). */
  clear: () => void;
  /** L'etiquette elle-meme. A rendre UNE fois par groupe de declencheurs. */
  overlay: ReactNode;
}

/**
 * LE HOOK D'UN GROUPE de declencheurs : une seule etiquette a la fois.
 *
 * UN ETAT PARTAGE, PAS UN PAR BOUTON, et c'est ce qui donne le bon geste :
 * glisser d'une icone a l'autre DEPLACE l'etiquette au lieu d'en fermer une et
 * d'en ouvrir une autre. Sept boutons avec sept etats locaux auraient aussi
 * fabrique sept portals.
 *
 * AUCUN DELAI, C'EST VOULU. Une etiquette qui attend est une etiquette qu'on
 * n'attend pas : la question qu'on se pose en survolant une icone ("c'est
 * laquelle ?") dure moins longtemps que n'importe quel delai raisonnable.
 * L'entree se fait en fondu (`--dur-base`, 200 ms), ce qui suffit a ce qu'une
 * souris qui TRAVERSE le groupe ne fasse pas clignoter sept etiquettes : le
 * fondu n'a pas le temps de se voir. La sortie, elle, est immediate.
 *
 * `enabled` a faux : aucun geste n'est pose, aucune etiquette n'est rendue. Le
 * hook reste appele (regle des hooks), il ne coute alors qu'un `useState`. Le
 * cas existe pour de vrai : le dock n'en veut pas quand son rail est deploye,
 * puisque le nom y est deja ecrit a cote de l'icone.
 */
export function useHoverLabel(enabled: boolean): HoverLabelBinding {
  const [state, setState] = useState<(HoverLabelPlacement & { label: string }) | null>(null);

  const clear = useCallback(() => setState(null), []);

  const montrer = useCallback((element: HTMLElement, label: string) => {
    const rect = element.getBoundingClientRect();
    setState({ ...hoverLabelPlacement(rect), label });
  }, []);

  const trigger = useCallback(
    (label: string): HoverLabelTriggerProps => ({
      onMouseEnter: (event) => montrer(event.currentTarget, label),
      onMouseLeave: clear,
      /* Le clic EMPORTE l'etiquette : il navigue, ou il ouvre un panneau
         par-dessus. La laisser flotter devant un ecran qui a change en ferait
         le nom de quelque chose qui n'est plus la. */
      onMouseDown: clear,
      /* Le clavier a droit au meme service que la souris : une tabulation
         dans un rail d'icones pose exactement le meme probleme. */
      onFocus: (event) => montrer(event.currentTarget, label),
      onBlur: clear,
    }),
    [clear, montrer],
  );

  const inerte: HoverLabelTriggerProps = {
    onMouseEnter: () => undefined,
    onMouseLeave: () => undefined,
    onMouseDown: () => undefined,
    onFocus: () => undefined,
    onBlur: () => undefined,
  };

  const visible = enabled && state !== null;

  return {
    trigger: enabled ? trigger : () => inerte,
    clear,
    overlay:
      visible && typeof document !== 'undefined'
        ? createPortal(
            <HoverLabelBubble label={state.label} top={state.top} left={state.left} />,
            document.body,
          )
        : null,
  };
}

/** La bulle. Separee du hook pour que le style se relise sans la mecanique. */
function HoverLabelBubble({
  label,
  top,
  left,
}: {
  label: string;
  top: number;
  left: number;
}): JSX.Element {
  return (
    <div
      aria-hidden
      className={cn(
        /* z-20 : au-dessus de la surface de travail et des popovers d'outils,
           SOUS le Modal (z-30) et tout ce qui est au-dessus de lui. Une
           etiquette de survol n'a jamais a passer devant une fenetre. */
        'taki-anim-fade pointer-events-none fixed z-20 max-w-[16rem] truncate',
        /* Le vocabulaire du popover du gabarit (ToolPopover) : meme bordure,
           meme fond, meme ombre. Une etiquette n'est pas un popover, mais elle
           se pose par-dessus la meme surface, et deux objets flottants qui se
           bordent differemment se remarquent. */
        'rounded-[var(--radius-sm)] border border-border bg-surface px-2 py-1',
        'text-[0.8125rem] font-medium text-ink-900 shadow-pop',
      )}
      style={{ top, left, transform: 'translateY(-50%)' }}
    >
      {label}
    </div>
  );
}
